import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Typography,
  useTheme,
  Menu,
  MenuItem,
  FormControl,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import { CheckCircle, Error, Search } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomIconButton from './CustomIconButton';
import add from 'packages/novous_web/src/assets/icons/plusBlue.svg';

interface CustomIconButtonOptions {
  text: string;
  onClick: () => void;
}

interface CustomDropDownInputProps {
  label?: string;
  name?: string;
  options: { value: string | number; label: string; description?: string }[];
  value: string | number | null;
  onChange: (selectedValue: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isCustomIconButton?: boolean;
  customIconLabel?: string;
  isMandatory?: boolean;
  status?: 'success' | 'error';
  customIconButton?: CustomIconButtonOptions;
  showDropdownArrow?: boolean;
  components?: {
    Option?: (props: any) => JSX.Element;
  };
}

const CustomDropDownInput: React.FC<CustomDropDownInputProps> = ({
  label,
  name,
  options,
  value,
  isCustomIconButton = false,
  onChange,
  customIconLabel = '',
  isMandatory = false,
  placeholder = 'Select a route',
  isDisabled = false,
  customIconButton,
  status,
  showDropdownArrow = false,
  components
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Debounce implementation
  const debouncedQuery = useDebounce(searchQuery, 300);

  const filteredOptions = useMemo(() => {
    const query = debouncedQuery.toLowerCase().trim();
    if (!query) return options;
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, debouncedQuery]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled) setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleSelect = (selectedValue: string | number) => {
    onChange(typeof selectedValue === 'number' ? selectedValue.toString() : selectedValue);
    handleClose();
  };

  // Maintain focus in search input
  useEffect(() => {
    if (anchorEl && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [anchorEl, filteredOptions.length]);

  const getIcon = () => {
    if (!value) return null;
    if (status === 'success') return <CheckCircle sx={{ color: 'green', fontSize: 20 }} />;
    if (status === 'error') return <Error sx={{ color: 'red', fontSize: 20 }} />;
    return null;
  };

  const selectedOption = options.find(o => o.value == value);

  return (
    <div className="flex flex-col w-full gap-2">
      <div className="flex justify-between items-center gap-2">
        <div className="flex justify-between gap-2 items-center w-full">
          {label && (
            <div className="flex gap-1">
              <Typography variant="body2" fontWeight={600} color="textPrimary">
                {label}
              </Typography>
              {isMandatory && <span className="text-red-500">*</span>}
            </div>
          )}
          {customIconButton && (
            <CustomIconButton
              icon={<img src={add} alt="add" width={16} height={16} />}
              text={customIconButton.text}
              background="transparent"
              outline={false}
              padding="0px"
              size={16}
              height="24px"
              variant="outlined"
              onClick={customIconButton.onClick}
            />
          )}
        </div>
        {isCustomIconButton && (
          <CustomIconButton
            icon={<img src={add} alt="add" width={16} height={16} />}
            text={customIconLabel}
            background="transparent"
            outline={false}
            padding="0px"
            height="24px"
            variant="outlined"
            onClick={() => console.log('Button clicked')}
          />
        )}
      </div>

      <div className="relative">
        <FormControl fullWidth>
          <Button
            onClick={handleOpen}
            disabled={isDisabled}
            sx={{
              borderRadius: '8px',
              border: `1px solid ${theme.palette.action.selected}`,
              padding: '10px 16px',
              height: '50px',
              textAlign: 'left',
              justifyContent: 'space-between',
              width: '100%',
              backgroundColor: isDisabled ? theme.palette.action.disabledBackground : 'white',
              color: selectedOption ? 'black' : theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Typography variant="body2" fontWeight={400}>
              {selectedOption?.label || placeholder}
            </Typography>
            {getIcon()}
            {showDropdownArrow && <ExpandMoreIcon sx={{ fontSize: 30, color: '#5547DD' }} />}
          </Button>
        </FormControl>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          disableAutoFocus
          disableEnforceFocus
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          MenuListProps={{
            autoFocus: false,
            autoFocusItem: false,
            disableListWrap: true,
            onBlur: (e) => e.stopPropagation(),
          }}
          PaperProps={{
            sx: {
              borderRadius: '8px',
              //   top: '10px!important',
              maxHeight: '300px',
              padding: '0px',
              overflowY: 'auto',
              boxShadow: '0 6px 10px rgba(0, 0, 0, 0.1)',
              //   width: anchorEl ? anchorEl.clientWidth : 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            },
          }}
        >
          <div style={{ padding: '8px 12px' }}>
            <TextField
              fullWidth
              autoFocus
              inputRef={searchInputRef}
              placeholder="Search..."
              size="small"
              variant="outlined"
              value={searchQuery}
              name={name}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: '6px',
                },
              }}
              onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
            />
          </div>

          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {filteredOptions.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" color="textSecondary">
                  No results found
                </Typography>
              </MenuItem>
            )}

            {filteredOptions.map((option, index) => {
              if (components?.Option) {
                // If custom Option component is passed, render it
                return (
                  <div
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    {components.Option({
                      data: option,
                      innerRef: null,
                      innerProps: {},
                      index,
                    })}
                  </div>
                );
              }

              // Default rendering if no custom Option component
              return (
                <MenuItem
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  sx={{
                    padding: '12px 16px',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  <Typography variant="body2" fontWeight={400}>
                    {option.label}
                  </Typography>
                  {option.description && (
                    <Typography variant="caption" color="textSecondary">
                      {option.description}
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </div>
        </Menu>
      </div>
    </div>
  );
};

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default CustomDropDownInput;
